/**
 * Clients — CRM agrégé depuis les factures émises.
 *
 * Design: reproduit fidèlement la page Clients de BE-TAYE.
 *   - Grille 3 colonnes de cartes clients avec Framer Motion (stagger)
 *   - Filtres : recherche, "récurrent uniquement", "actifs 3 mois"
 *   - Sheet latéral droit pour détails + historique factures
 *   - Toggle "Récurrent" dans le Sheet, persist via PATCH /clients/{id}
 *   - Badge source ("Native", "Axonaut", "Pennylane", "IOPOLE")
 *   - Icône PDF sur les factures avec file_asset_id (comme Factures)
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
SheetDescription, } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, Users, Building2, Mail, MapPin, FileText, Star,
  Download, TrendingUp, AlertCircle, Filter, RefreshCw, Save, Plus, Pencil, Upload,
  LayoutGrid, List, Clock, ArrowUpDown, Sparkles, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import InvoiceDetailSheet from "@/components/InvoiceDetailSheet";
import ClientEditorSheet from "@/components/ClientEditorSheet";
import SmartImportModal from "@/components/SmartImportModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtEuro(n, precision = 0) {
  return `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })} €`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

const SOURCE_STYLES = {
  native:    { label: "Native",    cls: "bg-blue-50 text-blue-700 border-blue-200" },
  axonaut:   { label: "Axonaut",   cls: "bg-orange-50 text-orange-700 border-orange-200" },
  pennylane: { label: "Pennylane", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  iopole:    { label: "IOPOLE",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const STATUS_META = {
  new:      { label: "🆕 Nouveau",   cls: "bg-sky-50 text-sky-700 border-sky-200" },
  vip:      { label: "🌟 VIP",       cls: "bg-purple-50 text-purple-700 border-purple-200" },
  active:   { label: "✅ Actif",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dormant:  { label: "💤 Dormant",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  at_risk:  { label: "⚠️ À risque",  cls: "bg-red-50 text-red-700 border-red-200" },
  prospect: { label: "🔍 Prospect",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

// ============================================================================
// KPI header
// ============================================================================
function KpiCards({ clients }) {
  const total = clients.length;
  const active = clients.filter((c) => {
    if (!c.last_invoice_date) return false;
    const d = new Date(c.last_invoice_date);
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 3);
    return d >= cutoff;
  }).length;
  const recurring = clients.filter((c) => c.is_recurring).length;
  const totalRevenue = clients.reduce((s, c) => s + c.revenue_total, 0);

  const cards = [
    { label: "Total clients", value: total, icon: Users, accent: "blue" },
    { label: "Actifs (3 mois)", value: active, icon: TrendingUp, accent: "emerald" },
    { label: "Récurrents", value: recurring, icon: Star, accent: "amber" },
    { label: "CA cumulé", value: fmtEuro(totalRevenue), icon: Building2, accent: "slate" },
  ];
  const accents = {
    blue:    "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    slate:   "bg-slate-100 text-slate-700",
  };
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c, i) => (
        <motion.div
          key={c.label}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.04 }}
        >
          <Card className="bg-white border-slate-200 rounded-xl p-5 card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", accents[c.accent])}>
                <c.icon className="h-4 w-4" strokeWidth={1.75} />
              </div>
            </div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              {c.label}
            </div>
            <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 font-mono tabular-nums"
                 style={{ fontFamily: "Manrope, sans-serif" }}>
              {c.value}
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

// ============================================================================
// Client card
// ============================================================================
function ClientCard({ client, onSelect, idx }) {
  const primarySource = client.sources?.[0] || "native";
  const src = SOURCE_STYLES[primarySource] || SOURCE_STYLES.native;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.02 }}
      onClick={() => onSelect(client)}
      className="cursor-pointer"
      data-testid={`client-card-${client.id}`}
    >
      <Card className="bg-white border-slate-200 rounded-xl p-5 card-hover h-full flex flex-col">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm truncate flex items-center gap-1.5"
                style={{ fontFamily: "Manrope, sans-serif" }}
                title={client.name}>
              {client.is_recurring && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />}
              {client.name}
            </h3>
            {client.city && (
              <p className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                {client.city}{client.postal_code && ` · ${client.postal_code}`}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge variant="outline" className={cn("text-[9px] uppercase", src.cls)}>
              {src.label}
            </Badge>
            {client.status && STATUS_META[client.status] && (
              <Badge variant="outline" className={cn("text-[9px]", STATUS_META[client.status].cls)}>
                {STATUS_META[client.status].label}
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2 pb-3 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">CA total</div>
            <div className="text-sm font-semibold font-mono tabular-nums text-slate-900">
              {fmtEuro(client.revenue_total)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">CA 12 mois</div>
            <div className="text-sm font-semibold font-mono tabular-nums text-blue-700">
              {fmtEuro(client.revenue_12m)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Factures</div>
            <div className="text-sm font-medium text-slate-800 font-mono tabular-nums">
              {client.invoice_count}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Panier moyen</div>
            <div className="text-sm font-medium text-slate-800 font-mono tabular-nums">
              {fmtEuro(client.avg_ticket)}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-slate-500">
            Dernière : <span className="font-mono text-slate-700">{fmtDate(client.last_invoice_date)}</span>
          </span>
          {client.overdue_ttc > 0.01 && (
            <span className="flex items-center gap-1 text-red-700 font-semibold font-mono">
              <AlertCircle className="h-3 w-3" />
              {fmtEuro(client.overdue_ttc)}
            </span>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

// ============================================================================
// Detail sheet
// ============================================================================
function ClientSheet({ client, open, onClose, onReload, onOpenInvoice, onEdit, canWrite = true }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);

  useEffect(() => {
    if (!client || !open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/clients/${client.id}`);
        setDetail(data);
        setNotes(data?.override?.notes || "");
        setIsRecurring(!!data?.client?.is_recurring);
      } catch (e) {
        toast.error(apiError(e));
        onClose();
      } finally { setLoading(false); }
    })();
  }, [client, open]);

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        notes: notes || null,
        is_recurring: isRecurring,
      });
      toast.success("Modifications enregistrées");
      onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[540px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <SheetTitle className="text-lg text-slate-900 truncate"
                           style={{ fontFamily: "Manrope, sans-serif" }}>
                {client?.name}
              </SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription>
              <p className="text-xs text-slate-500">
                {client?.invoice_count} facture{client?.invoice_count > 1 ? "s" : ""} · CA total {fmtEuro(client?.revenue_total)}
              </p>
            </div>
            {onEdit && (
              <Button variant="outline" size="sm" onClick={onEdit}
                       className="border-slate-200 shrink-0"
                       data-testid="client-sheet-edit-btn">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifier
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-5">
          {loading ? (
            <div className="skeleton h-24 rounded-lg" />
          ) : detail ? (
            <div className="space-y-6">
              {/* KPI mini */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-blue-700 font-medium">CA 12m</div>
                  <div className="text-sm font-semibold font-mono tabular-nums text-blue-900 mt-0.5">
                    {fmtEuro(detail.client.revenue_12m)}
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">Reste dû</div>
                  <div className="text-sm font-semibold font-mono tabular-nums text-amber-900 mt-0.5">
                    {fmtEuro(detail.client.outstanding_ttc)}
                  </div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-medium">En retard</div>
                  <div className="text-sm font-semibold font-mono tabular-nums text-red-900 mt-0.5">
                    {fmtEuro(detail.client.overdue_ttc)}
                  </div>
                </div>
              </div>

              {/* Coordonnées */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Coordonnées</h4>
                <div className="space-y-1 text-sm text-slate-700">
                  {detail.client.email && (
                    <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{detail.client.email}</div>
                  )}
                  {detail.client.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <span>{detail.client.address}{detail.client.postal_code && `, ${detail.client.postal_code}`}{detail.client.city && ` ${detail.client.city}`}</span>
                    </div>
                  )}
                  {detail.client.siret && (
                    <div className="flex items-center gap-2 font-mono text-xs"><Building2 className="h-3.5 w-3.5 text-slate-400" />SIRET {detail.client.siret}</div>
                  )}
                  {detail.client.vat_number && (
                    <div className="flex items-center gap-2 font-mono text-xs text-slate-500">TVA {detail.client.vat_number}</div>
                  )}
                </div>
              </div>

              {/* Modifiables — gated on invoices.write */}
              {canWrite && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      Client récurrent
                    </label>
                    <Switch checked={isRecurring} onCheckedChange={setIsRecurring}
                      data-testid="client-recurring-toggle" />
                  </div>
                  <div>
                    <label htmlFor="client-notes" className="text-xs uppercase tracking-wider text-slate-500 font-medium">Notes internes</label>
                    <Textarea id="client-notes" name="client-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Contact commercial, préférences, remarques…"
                      className="mt-1 min-h-[80px] bg-white"
                      data-testid="client-notes" />
                  </div>
                  <Button onClick={save} disabled={saving} size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="client-save-btn">
                    {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Enregistrer
                  </Button>
                </div>
              )}

              {/* Historique factures */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                  Historique ({detail.invoices.length})
                </h4>
                <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                  {detail.invoices.map((inv) => (
                    <button key={inv.id}
                      type="button"
                      onClick={() => onOpenInvoice?.(inv.id)}
                      className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors text-left cursor-pointer"
                      data-testid={`client-invoice-row-${inv.id}`}>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-mono text-blue-700 hover:underline block truncate">
                          {inv.number || "brouillon"}
                        </span>
                        <div className="text-[10px] text-slate-500 font-mono">{fmtDate(inv.issued_at)}</div>
                      </div>
                      <Badge variant="outline" className={cn("text-[9px] uppercase",
                        inv.status === "paid" ? "status-paid" :
                        inv.status === "issued" ? "status-facture" :
                        inv.status === "draft" ? "status-draft" : "status-inactive")}>
                        {({paid:"Payée",issued:"Émise",draft:"Brouillon",cancelled:"Annulée",imported:"Importée",overdue:"En retard",partially_paid:"Part. payée"}[inv.status]) || inv.status}
                      </Badge>
                      <div className="text-xs font-mono tabular-nums font-semibold text-slate-900 w-24 text-right">
                        {fmtEuro(inv.total_ttc, 2)}
                      </div>
                      {inv.file_asset_id && (
                        <a href={`${API}/files/${inv.file_asset_id}`}
                           target="_blank" rel="noreferrer"
                           className="text-slate-400 hover:text-blue-600 shrink-0"
                           onClick={(e) => e.stopPropagation()}
                           data-testid={`inv-pdf-${inv.id}`}>
                          <FileText className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Main page
// ============================================================================
export default function Clients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [clients, setClients] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [onlyRecurring, setOnlyRecurring] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");   // all | new | vip | active | dormant | at_risk | prospect
  const [sourceFilter, setSourceFilter] = useState("all");
  const [revenueBucket, setRevenueBucket] = useState("all"); // all | lt1k | 1k_10k | 10k_100k | gt100k
  const [sortBy, setSortBy] = useState("revenue_total");
  const [sortDir, setSortDir] = useState("desc");
  const [view, setView] = useState("cards");                 // cards | table
  const [selected, setSelected] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const openInvoice = (id) => { setInvoiceId(id); setInvoiceOpen(true); };
  const openEditor = (id = null) => { setEditingClientId(id); setEditorOpen(true); };

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        only_recurring: onlyRecurring, only_active: onlyActive,
        only_overdue: onlyOverdue, sort_by: sortBy, sort_dir: sortDir,
      };
      if (statusFilter !== "all") params.status = statusFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (revenueBucket === "lt1k") params.max_revenue = 1000;
      else if (revenueBucket === "1k_10k") { params.min_revenue = 1000; params.max_revenue = 10000; }
      else if (revenueBucket === "10k_100k") { params.min_revenue = 10000; params.max_revenue = 100000; }
      else if (revenueBucket === "gt100k") params.min_revenue = 100000;
      const { data } = await api.get("/clients", { params });
      setClients(data.clients);
      setStatusCounts(data.status_counts || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    onlyRecurring, onlyActive, onlyOverdue, statusFilter, sourceFilter,
    revenueBucket, sortBy, sortDir,
  ]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.city || "").toLowerCase().includes(q) ||
      (c.siret || "").includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  }, [clients, query]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir(col === "name" ? "asc" : "desc"); }
  };

  const exportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = filtered.map((c) => ({
        Nom: c.name, Ville: c.city, CP: c.postal_code, Email: c.email, SIRET: c.siret,
        Statut: STATUS_META[c.status]?.label.replace(/^[^\w]+ /, "") || c.status,
        Source: c.sources?.join(", ") || c.source,
        "CA total": c.revenue_total, "CA 12m": c.revenue_12m,
        Factures: c.invoice_count, "Panier moyen": c.avg_ticket,
        "Reste dû": c.outstanding_ttc, "En retard": c.overdue_ttc,
        "DSO (j)": c.dso_days, "Dernière facture": c.last_invoice_date,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Clients");
      XLSX.writeFile(wb, `clients_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`${rows.length} clients exportés`);
    } catch (e) { toast.error("Export échoué"); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Clients
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Fiches clients + agrégation temps réel du CA depuis vos factures.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={exportExcel} variant="outline"
                     data-testid="clients-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Exporter Excel
            </Button>
            <Button onClick={() => setImportOpen(true)}
                     variant="outline"
                     data-testid="clients-import-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Importer Excel
            </Button>
            <Button onClick={() => openEditor(null)}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="clients-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau client
            </Button>
          </div>
        )}
      </div>

      <KpiCards clients={clients} />

      {/* Status chips segmentation */}
      <div className="flex flex-wrap gap-1.5" data-testid="status-chips">
        {[
          { key: "all",       label: "Tous",       count: clients.length },
          { key: "vip",       label: "🌟 VIP",     count: statusCounts.vip },
          { key: "active",    label: "✅ Actifs",  count: statusCounts.active },
          { key: "at_risk",   label: "⚠️ À risque", count: statusCounts.at_risk },
          { key: "dormant",   label: "💤 Dormants", count: statusCounts.dormant },
          { key: "new",       label: "🆕 Nouveaux", count: statusCounts.new },
          { key: "prospect",  label: "🔍 Prospects", count: statusCounts.prospect },
        ].map((s) => (
          <button key={s.key}
                  onClick={() => setStatusFilter(s.key)}
                  data-testid={`chip-${s.key}`}
                  className={cn(
                    "text-xs px-3 py-1.5 rounded-full border transition-colors",
                    statusFilter === s.key
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            {s.label} <span className="ml-1 opacity-70 font-mono">{s.count || 0}</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            id="clients-search" name="clients-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher (nom, ville, SIRET, email…)"
            className="pl-8 bg-white border-slate-200"
            data-testid="clients-search"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select id="clients-source-filter" name="clients-source-filter"
                  value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                  className="text-xs px-2 py-2 rounded-md border border-slate-200 bg-white"
                  data-testid="source-filter">
            <option value="all">Toutes sources</option>
            <option value="native">Native</option>
            <option value="axonaut">Axonaut</option>
            <option value="pennylane">Pennylane</option>
            <option value="iopole">IOPOLE</option>
          </select>
          <select id="clients-revenue-filter" name="clients-revenue-filter"
                  value={revenueBucket} onChange={(e) => setRevenueBucket(e.target.value)}
                  className="text-xs px-2 py-2 rounded-md border border-slate-200 bg-white"
                  data-testid="revenue-filter">
            <option value="all">Toutes tranches CA</option>
            <option value="lt1k">&lt; 1 k€</option>
            <option value="1k_10k">1–10 k€</option>
            <option value="10k_100k">10–100 k€</option>
            <option value="gt100k">&gt; 100 k€</option>
          </select>
          <button onClick={() => setOnlyOverdue(!onlyOverdue)}
                  data-testid="filter-overdue"
                  className={cn(
                    "text-xs px-3 py-2 rounded-md border transition-colors flex items-center gap-1.5",
                    onlyOverdue
                      ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            <AlertCircle className="h-3.5 w-3.5" />En retard
          </button>
          <button onClick={() => setOnlyActive(!onlyActive)}
                  data-testid="filter-active"
                  className={cn(
                    "text-xs px-3 py-2 rounded-md border transition-colors flex items-center gap-1.5",
                    onlyActive
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            <TrendingUp className="h-3.5 w-3.5" />Actifs (3m)
          </button>
          <button onClick={() => setOnlyRecurring(!onlyRecurring)}
                  data-testid="filter-recurring"
                  className={cn(
                    "text-xs px-3 py-2 rounded-md border transition-colors flex items-center gap-1.5",
                    onlyRecurring
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            <Star className="h-3.5 w-3.5" />Récurrents
          </button>
          <div className="ml-auto flex rounded-md border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => setView("cards")}
                    data-testid="view-cards"
                    className={cn("px-2.5 py-1.5", view === "cards" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50")}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => setView("table")}
                    data-testid="view-table"
                    className={cn("px-2.5 py-1.5", view === "table" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50")}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Grid or Table */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton h-48 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-16 text-center bg-white border-slate-200 rounded-xl">
          <Users className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">
            {clients.length === 0
              ? "Aucun client encore. Créez une facture pour commencer."
              : "Aucun résultat pour ces filtres."}
          </p>
        </Card>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c, i) => (
            <ClientCard key={c.id} client={c} onSelect={() => navigate(`/app/clients/${c.id}`)} idx={i} />
          ))}
        </div>
      ) : (
        <Card className="bg-white border-slate-200 rounded-xl overflow-hidden" data-testid="clients-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
                <tr>
                  {[
                    { k: "name", label: "Client" },
                    { k: "city", label: "Ville" },
                    { k: "status", label: "Statut" },
                    { k: "revenue_total", label: "CA total", right: true },
                    { k: "revenue_12m", label: "CA 12m", right: true },
                    { k: "invoice_count", label: "Fact.", right: true },
                    { k: "avg_ticket", label: "Panier", right: true },
                    { k: "overdue_ttc", label: "Retard", right: true },
                    { k: "dso_days", label: "DSO j", right: true },
                    { k: "last_invoice_date", label: "Dernière" },
                    { k: "sources", label: "Source" },
                  ].map((h) => (
                    <th key={h.k}
                        onClick={() => toggleSort(h.k)}
                        className={cn("px-3 py-2 font-semibold cursor-pointer select-none hover:bg-slate-100",
                          h.right && "text-right")}
                        data-testid={`sort-${h.k}`}>
                      <span className="inline-flex items-center gap-1">
                        {h.label}
                        {sortBy === h.k && <ArrowUpDown className="w-3 h-3" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/app/clients/${c.id}`)}
                       className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                       data-testid={`client-row-${c.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {c.is_recurring && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        <span className="font-medium text-slate-900 truncate max-w-[220px]" title={c.name}>{c.name}</span>
                      </div>
                      {c.email && <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{c.email}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{c.city || "—"}</td>
                    <td className="px-3 py-2">
                      {c.status && STATUS_META[c.status] && (
                        <Badge variant="outline" className={cn("text-[9px]", STATUS_META[c.status].cls)}>
                          {STATUS_META[c.status].label}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtEuro(c.revenue_total)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{fmtEuro(c.revenue_12m)}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.invoice_count}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtEuro(c.avg_ticket)}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {c.overdue_ttc > 0.01
                        ? <span className="text-red-700 font-semibold">{fmtEuro(c.overdue_ttc)}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {c.dso_days == null
                        ? <span className="text-slate-400">—</span>
                        : <span className={c.dso_days > 15 ? "text-red-700 font-semibold" : c.dso_days > 0 ? "text-amber-700" : "text-emerald-700"}>
                            {c.dso_days > 0 ? `+${c.dso_days}` : c.dso_days}
                          </span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 font-mono text-xs">{fmtDate(c.last_invoice_date)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={cn("text-[9px] uppercase",
                        SOURCE_STYLES[c.sources?.[0]]?.cls || SOURCE_STYLES.native.cls)}>
                        {SOURCE_STYLES[c.sources?.[0]]?.label || "Native"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ClientSheet
        client={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onReload={load}
        onOpenInvoice={openInvoice}
        onEdit={canWrite ? () => { openEditor(selected?.id); setSelected(null); } : null}
        canWrite={canWrite}
      />

      {/* Sheet facture — même composant que dans /app/invoices */}
      <InvoiceDetailSheet
        invoiceId={invoiceId}
        open={invoiceOpen}
        onOpenChange={(v) => { setInvoiceOpen(v); if (!v) setInvoiceId(null); }}
      />

      {/* Sheet création / édition client */}
      <ClientEditorSheet
        clientId={editingClientId}
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditingClientId(null); }}
        onSaved={() => load()}
      />

      <SmartImportModal isOpen={importOpen} onClose={() => setImportOpen(false)}
                          onSuccess={() => load()} kind="clients" />
    </div>
  );
}
